/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Container from '@material-ui/core/Container'
import Button from '@material-ui/core/Button'
import {
  Star,
  COACH_PROFILE_INLINE_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import IconSocial from '@components/atoms/icons/icon-social'
import Embed from '@components/embed/embed'
import CoachProfileLeadForm from '@components/coach-profile-lead-form'
import secureLink from '@utils/secureLink'
import remarkGfm from 'remark-gfm'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useRouter } from 'next/router'
import useIsMobile from '@hooks/useIsMobile'
import { gtmEvent } from '@lib/gtm-utils'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import CoachAccreditationsRow, {
  coachAccreditationsRowHasContent,
} from '@components/coach-accreditations-row'
import CoachSpecializedSkillsRow, {
  coachSpecializedSkillsRowHasContent,
} from '@components/coach-specialized-skills-row'
import CoachToolsRow, {
  coachToolsRowHasContent,
} from '@components/coach-tools-row'
import CoachProfileOfferingsShop from '@components/coach-profile-offerings-shop'
import CoachProfileBookingWidget, {
  getBookablePrivateLessonOfferingRows,
  getCoachPrivateScheduleUrl,
} from '@components/coach-profile-booking-widget'
import {
  getCoachMembershipDisplayAccreditation,
} from '@lib/pga-coach/coach-credentials'
import { CoachProfileInColumn } from '@lib/pga-coach/coach-profile-layout'
import {
  flattenUniqueCoachProfileOfferings,
  getCoachProfilePersonaShopOfferings,
} from '@lib/pga-coach/offering-search'
import { extractYouTubeVideoId } from '@lib/pga-coach/youtube'
import CoachAccreditationExplainerDialog from '@components/coach-accreditation-explainer-dialog'
import CoachMembershipBanner from '@components/coach-membership-banner'
import {
  FollowersAndSocial,
  FollowersWrapper,
  BioWrapper,
  SocialIcon,
  useStyles,
} from './coach-bio.styles'
import {
  showCoachMeetContactTabs,
  buildCoachProfileBookingCardNavItems,
  preserveQueryOnHref,
  scrollCoachProfileSectionIntoViewWhenPresent,
  isCoachProfilePersonaTabId,
  coachExternalBookingUrl,
  COACH_PROFILE_OFFERINGS_ANCHOR_ID,
  COACH_PROFILE_CONTACT_ANCHOR_ID,
  PERSONA_PROFILE_TAB_LABELS,
} from './coach-profile-tab-utils'

/**
 * Bios beyond this character count get truncated and gain a Read More
 * toggle so the bio area doesn't dominate About. Tuned against the
 * longest bios in production (~1500 chars); anything shorter has no
 * clamp UI. Read More stays inline in the last visible paragraph when
 * that cut is not a hyperlink; Read Less always follows the full
 * expanded bio (lists included).
 */
const BIO_CLAMP_CHAR_THRESHOLD = 380

const HONORIFIC_ABBREVIATIONS = new Set([
  'dr', 'mr', 'mrs', 'ms', 'mx', 'jr', 'sr', 'st', 'prof', 'rev',
])

const HTML_BLOCK_CLOSE = /<\/(?:p|h[1-6]|ul|ol|blockquote)>/i
const TRAILING_HTML_ANCHOR = /(?:<p\b[^>]*>\s*)?<a\b[^>]*>[\s\S]*?<\/a>(?:\s*<\/p>)?\s*$/i
const TRAILING_MARKDOWN_LINK = /(?<!!)\[[^\]]+\]\([^)]+\)\s*$/

function splitBioBlocks(text) {
  if (HTML_BLOCK_CLOSE.test(text)) {
    return text
      .split(/(?<=<\/(?:p|h[1-6]|ul|ol|blockquote)>)\s*/i)
      .filter((block) => block.trim())
  }
  return text.split(/\n{2,}/)
}

function joinBioBlocks(blocks) {
  return blocks.join('\n\n')
}

function isMarkdownParagraphBlock(block) {
  const first = block.trimStart()
  if (/^<(ul|ol|li|h[1-6]|blockquote|pre|table)\b/i.test(first)) return false
  return !/^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```)/.test(first)
}

function unwrapParagraphMarkup(block) {
  return block.trim()
    .replace(/^<p\b[^>]*>\s*/i, '')
    .replace(/\s*<\/p>$/i, '')
    .trim()
}

function trailingHyperlinkMatch(text) {
  if (typeof text !== 'string' || !text) return null
  const trimmed = text.trimEnd()
  const html = trimmed.match(TRAILING_HTML_ANCHOR)
  if (html) return html[0]
  const markdown = trimmed.match(TRAILING_MARKDOWN_LINK)
  return markdown ? markdown[0] : null
}

export function coachBioEndsWithHyperlink(text) {
  return Boolean(trailingHyperlinkMatch(text))
}

function isLinkOnlyBlock(block) {
  const inner = unwrapParagraphMarkup(block)
  return /^(?:\[[^\]]+\]\([^)]+\)|<a\b[^>]*>[\s\S]*?<\/a>)$/i.test(inner)
}

function tokenBefore(text, index) {
  let i = index - 1
  while (i >= 0 && /[A-Za-z]/.test(text[i])) i -= 1
  return text.slice(i + 1, index)
}

function isHonorificAbbreviation(text, periodIndex) {
  return HONORIFIC_ABBREVIATIONS.has(tokenBefore(text, periodIndex).toLowerCase())
}

function isRealShortSentence(text, endIndex) {
  if (text[endIndex] === '!' || text[endIndex] === '?') return true
  return /\s/.test(text.slice(0, endIndex).trim())
}

/**
 * Last `.` / `!` / `?` that ends a real sentence, not an honorific
 * (`Dr. `, `Mr. `, `Jr. `). Matches below `minBreak` are ignored unless
 * they are a real short first sentence (`Hello world.`), not an abbreviation.
 */
function lastCompleteSentenceEnd(text, minBreak = 0) {
  let lastAtOrAfterMin = -1
  let lastShortReal = -1
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    const next = text[i + 1]
    const isSentenceEnd = (ch === '.' || ch === '!' || ch === '?')
      && (next === undefined || /\s/.test(next))
      && !(ch === '.' && isHonorificAbbreviation(text, i))
    if (isSentenceEnd && i >= minBreak) {
      lastAtOrAfterMin = i
    } else if (isSentenceEnd && isRealShortSentence(text, i)) {
      lastShortReal = i
    }
  }
  return lastAtOrAfterMin >= 0 ? lastAtOrAfterMin : lastShortReal
}

/**
 * If a clamp would end on/after a bio hyperlink, cut at the previous
 * paragraph or sentence that is not a link so Read More is never
 * "My Link Read More" on one line. When no safe earlier cut exists,
 * the original text is returned and the UI places Read More on its
 * own line after the truncated block.
 */
function cutBeforeTrailingHyperlink(text, minBreak) {
  if (!coachBioEndsWithHyperlink(text)) return text

  const blocks = splitBioBlocks(text)
  if (blocks.length > 1 && isLinkOnlyBlock(blocks[blocks.length - 1])) {
    return joinBioBlocks(blocks.slice(0, -1)).trimEnd()
  }

  const match = trailingHyperlinkMatch(text)
  if (!match) return text
  const beforeLink = text.slice(0, text.length - match.length).trimEnd()
  const sentenceEnd = lastCompleteSentenceEnd(beforeLink, minBreak)
  if (sentenceEnd >= 0) {
    return beforeLink.slice(0, sentenceEnd + 1).trimEnd()
  }
  if (beforeLink.length >= minBreak) return beforeLink
  return text
}

function dropIncompleteTrailingParagraph(text) {
  const blocks = text.split(/\n{2,}/)
  if (blocks.length < 2) return text
  const last = blocks[blocks.length - 1].trim()
  if (/[.!?]"?$/.test(last)) return text
  blocks.pop()
  return blocks.join('\n\n')
}

/**
 * Truncate a long bio so Read More can sit in the last *visible*
 * paragraph. Prefers complete markdown/HTML paragraphs, then the last
 * complete sentence (skipping honorifics such as Dr./Mr./Jr.); only
 * mid-sentence cuts use an ellipsis. A short first sentence such as
 * "Hello world." still truncates there. Word-boundary slices that
 * would leave an orphan stub (e.g. "My…") drop that leftover block
 * instead of rendering it as its own line. Trailing bio hyperlinks
 * are cut away so Read More is not adjacent to a bio `<a>`.
 */
export function truncateCoachBio(bio, maxChars = BIO_CLAMP_CHAR_THRESHOLD) {
  if (typeof bio !== 'string') return ''
  const text = bio.trim()
  if (text.length <= maxChars) return text

  const minBreak = Math.min(40, Math.floor(maxChars / 2))
  const paragraphs = splitBioBlocks(text)
  const kept = []
  for (let i = 0; i < paragraphs.length; i += 1) {
    const para = paragraphs[i]
    const candidate = kept.length ? `${joinBioBlocks(kept)}\n\n${para}` : para
    if (candidate.length > maxChars) break
    kept.push(para)
  }
  if (kept.length > 0) {
    return cutBeforeTrailingHyperlink(joinBioBlocks(kept), minBreak)
  }

  const first = paragraphs[0] || text
  const slice = first.slice(0, maxChars)
  const sentenceEnd = lastCompleteSentenceEnd(slice, minBreak)
  if (sentenceEnd >= 0) {
    return cutBeforeTrailingHyperlink(
      first.slice(0, sentenceEnd + 1).trimEnd(),
      minBreak,
    )
  }

  const lastBreak = Math.max(slice.lastIndexOf(' '), slice.lastIndexOf('\n'))
  const cut = lastBreak >= minBreak ? slice.slice(0, lastBreak) : slice
  const trimmed = dropIncompleteTrailingParagraph(
    cut.trimEnd().replace(/[.,;:]+$/, ''),
  )
  if (trimmed.length > 0 && trimmed.length < text.length) {
    return cutBeforeTrailingHyperlink(`${trimmed}…`, minBreak)
  }
  return cutBeforeTrailingHyperlink(
    `${cut.trimEnd().replace(/[.,;:]+$/, '')}…`,
    minBreak,
  )
}

function countMarkdownParagraphs(source) {
  if (typeof source !== 'string' || !source.trim()) return 0
  return splitBioBlocks(source.trim()).filter(isMarkdownParagraphBlock).length
}

function CoachBioMarkdown({
  source,
  trailingControl = null,
  className = undefined,
  placeToggleAfter = false,
}) {
  const paragraphTotal = countMarkdownParagraphs(source)
  let renderedParagraphs = 0
  const injectToggle = Boolean(trailingControl)
  const afterContent = injectToggle && (
    placeToggleAfter
    || paragraphTotal === 0
    || coachBioEndsWithHyperlink(source)
    || HTML_BLOCK_CLOSE.test(source)
  )
  const inlineInLastP = injectToggle && !afterContent
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={inlineInLastP ? {
          p: ({ children }) => {
            renderedParagraphs += 1
            const isLast = renderedParagraphs === paragraphTotal
            return (
              <p>
                {children}
                {isLast ? (
                  <>
                    {' '}
                    {trailingControl}
                  </>
                ) : null}
              </p>
            )
          },
        } : undefined}
      >
        {source}
      </ReactMarkdown>
      {afterContent ? trailingControl : null}
    </div>
  )
}

CoachBioMarkdown.propTypes = {
  source: PropTypes.string.isRequired,
  trailingControl: PropTypes.node,
  className: PropTypes.string,
  placeToggleAfter: PropTypes.bool,
}

/**
 * Google Maps search URL so desktop browsers can open the facility
 * (a `geo:` URI is ignored there). Same query encoding as
 * `googleMapsPlaceUrl`. Phones still hand this https link off to Maps.
 */
export function buildFacilityMapsHref(facility) {
  if (!facility) return null
  const name = facility.facilityName?.trim()
  const street = facility.street?.trim()
  const cityState = [facility.city, facility.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ')
  const query = [name, street, cityState].filter(Boolean).join(', ')
  if (!query) return null
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

function CoachBio({
  coach,
  profileTab: profileTabProp,
  onProfileTabChange,
  onScrollToSection,
  offeringsLoading,
  offeringsByPersona,
  coachAccreditationsEnabled,
  hasPgaHopeProgram = false,
  children,
}) {
  const {
    coachProfileId,
    firstName,
    lastName,
    displayName,
    certificationLevel,
    memberType,
    title,
    profilePhoto,
    city,
    state,
    bio,
    socialJsonData,
    facilities,
    introductionVideoUrl,
    bookable,
    /**
     * Tenure stat in the About bio footer. Reserved as a stub: the
     * coach-profile API does not expose this field yet
     * (`src/schema/coach-profile.js` does not select it), so the
     * stat block stays hidden until the backend / membership system
     * surfaces an `electionYear`, `membershipStartYear`, or
     * precomputed `yearsOfPgaMembership` value through the gateway.
     * When available, value is the already-computed integer year
     * count (e.g. `12` → "12 years").
     */
    yearsOfPgaMembership,
    coach: {
      id, preferences = {}, freeTierLimitsReached = [],
    } = {},
  } = coach
  const isLeadsLimitReached = freeTierLimitsReached.includes('LEADS')
  const { acceptsLeads } = preferences
  const showContactForm = acceptsLeads && !isLeadsLimitReached

  const [profileTabInternal, setProfileTabInternal] = useState('meet')
  const [openAccreditationId, setOpenAccreditationId] = useState(null)
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const openAccreditationExplainer = (source) => (accreditationId) => {
    trackCoachProfileEvent('click-coach-profile-accreditation', {
      accreditation_id: accreditationId,
      source,
    })
    setOpenAccreditationId(accreditationId)
  }
  const [isBioExpanded, setIsBioExpanded] = useState(false)
  const bioIsLong = typeof bio === 'string' && bio.length > BIO_CLAMP_CHAR_THRESHOLD
  const displayedBio = bioIsLong && !isBioExpanded
    ? truncateCoachBio(bio)
    : bio

  const videoId = introductionVideoUrl
    ? extractYouTubeVideoId(introductionVideoUrl)
    : null

  const videoProps = {
    provider: 'youtube',
    id: videoId,
    autoPlay: false,
  }

  const showIntroductionVideo = !!videoProps.id

  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useIsMobile()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const router = useRouter()

  useEffect(() => {
    (window.dataLayer || []).push({ event: 'optimize.activate' })
  }, [])

  const profileIsActive = coach.status === 'ACTIVE'
  const baseShowProfileTabs = showCoachMeetContactTabs(coach, isDesktop)
  const allOfferings = useMemo(
    () => flattenUniqueCoachProfileOfferings(offeringsByPersona),
    [offeringsByPersona],
  )
  const bookablePrivateLessons = useMemo(
    () => getBookablePrivateLessonOfferingRows(allOfferings),
    [allOfferings],
  )
  const externalBookingUrl = coachExternalBookingUrl(coach)
  const hasShopOfferings = allOfferings.length > 0
  const showViewServices = Boolean(externalBookingUrl)
    || hasShopOfferings
    || (!externalBookingUrl && offeringsLoading)

  /**
   * `baseShowProfileTabs` is the eligibility flag (active coach with a
   * meet section who collects leads or has bookings). Keep that for the
   * in-page lead-form region. The flagged profile does not render a
   * full-width About / Services / Photos / Testimonials / Credentials
   * strip. Small viewports put those jumps on the booking card.
   * Contact is `#coach-profile-contact` on About, not a routed tab.
   */
  const showProfileTabs = baseShowProfileTabs

  const isProfileTabControlled = showProfileTabs && typeof onProfileTabChange === 'function'
  const profileTab = isProfileTabControlled ? (profileTabProp ?? 'meet') : profileTabInternal
  const setProfileTab = isProfileTabControlled ? onProfileTabChange : setProfileTabInternal
  const isPersonaTab = isCoachProfilePersonaTabId(profileTab)
  const showAboutSurface = !showProfileTabs || !isPersonaTab
  const showBookingWidget = showAboutSurface
    && !externalBookingUrl
    && bookablePrivateLessons.length > 0
    && Boolean(getCoachPrivateScheduleUrl(coach))

  useEffect(() => {
    if (!showProfileTabs || showContactForm || profileTab !== 'contact') return
    setProfileTab('meet')
  }, [showProfileTabs, showContactForm, profileTab, setProfileTab])

  const pendingSectionScrollRef = useRef(null)

  const scrollToProfileSection = useCallback((anchorId) => {
    if (!anchorId) return
    if (onScrollToSection) {
      onScrollToSection(anchorId)
      return
    }
    if (isPersonaTab) {
      pendingSectionScrollRef.current = anchorId
      setProfileTab('meet')
      return
    }
    scrollCoachProfileSectionIntoViewWhenPresent(anchorId)
  }, [onScrollToSection, isPersonaTab, setProfileTab])

  useEffect(() => {
    const anchorId = pendingSectionScrollRef.current
    if (!anchorId || profileTab !== 'meet') return
    pendingSectionScrollRef.current = null
    scrollCoachProfileSectionIntoViewWhenPresent(anchorId)
  }, [profileTab])

  /**
   * Tenure stat is rendered only when the API surfaces a numeric
   * `yearsOfPgaMembership` >= 1. Treat 0 / null / undefined / non-numeric
   * as absent so the stat block silently drops out for non-PGA-member
   * coaches and for member coaches in their first year of induction.
   */
  const hasYearsOfMembership = Number.isFinite(yearsOfPgaMembership) && yearsOfPgaMembership >= 1
  const hasStatsStrip = hasYearsOfMembership
  const hasSocialLinks = Array.isArray(socialJsonData) && socialJsonData.length > 0

  const renderStatsStrip = (className) => (
    hasStatsStrip ? (
      <FollowersAndSocial className={className}>
        <FollowersWrapper className={classes.followersRow}>
          <div className={`bioFigures ${classes.bioStatRow}`}>
            <Box className={classes.bioStatIconWell} aria-hidden>
              <Star {...COACH_PROFILE_INLINE_ICON_PROPS} />
            </Box>
            <div className={classes.bioStatBlock}>
              <Typography variant="overline" className={classes.bioStatLabel} component="div">
                PGA Member
              </Typography>
              <Typography variant="body1" component="p" className={classes.bioStatValue}>
                {`${yearsOfPgaMembership} ${yearsOfPgaMembership === 1 ? 'year' : 'years'}`}
              </Typography>
            </div>
          </div>
        </FollowersWrapper>
      </FollowersAndSocial>
    ) : null
  )

  const renderSocialLinks = (className, testId) => (
    hasSocialLinks ? (
      <Box
        className={className}
        data-testid={testId}
        component="section"
        aria-label="Follow this coach"
      >
        <Box className={classes.socialPromptRow}>
          <Typography
            variant="body2"
            className={classes.socialPrompt}
            component="p"
          >
            Follow me for golf tips and updates.
          </Typography>
        </Box>
        <Box className={classes.socialIconsRow}>
          {socialJsonData.map((social) => (
            <SocialIcon
              aria-label={social.type}
              key={social.type}
              href={social.reference}
              target="_blank"
              className={classes.socialIconInCard}
              onClick={() => {
                gtmEvent({
                  event: 'click-coach-social',
                  attributes: {
                    platform: social.type,
                    coach_name: displayName || `${firstName} ${lastName}`,
                    coach_profile_id: coachProfileId,
                  },
                })
                trackCoachProfileEvent('click-coach-profile-social', { platform: social.type })
              }}
            >
              <IconSocial
                type={social.type}
                target="_blank"
                fill="transparent"
              />
            </SocialIcon>
          ))}
        </Box>
      </Box>
    ) : null
  )

  const showAccreditationsRow = coachAccreditationsEnabled && showAboutSurface
  const showAccreditationsInBio = showAccreditationsRow
    && coachAccreditationsRowHasContent({ ...coach, hasPgaHopeProgram })
  const showSpecializedSkillsInBio = showAboutSurface
    && coachSpecializedSkillsRowHasContent(coach)
  const showToolsInBio = showAboutSurface && coachToolsRowHasContent(coach)
  const bookableLocations = useMemo(() => {
    const byKey = new Map()
    bookablePrivateLessons.forEach((offering) => {
      const name = offering?.venueName?.trim() || ''
      const address = offering?.coachFacilityFullAddress?.trim()
        || [offering?.city, offering?.state].map((part) => part?.trim()).filter(Boolean).join(', ')
      const key = String(offering?.facilityId || `${name}|${address}`).trim()
      if ((!name && !address) || byKey.has(key)) return
      const destination = [name, address.replace(/\s*\n\s*/g, ', ')].filter(Boolean).join(', ')
      byKey.set(key, {
        key,
        name,
        address,
        href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`,
      })
    })
    return [...byKey.values()]
  }, [bookablePrivateLessons])
  const showLocationsInBio = showAboutSurface && bookableLocations.length > 0
  const detailsColumnCount = [showLocationsInBio, showAccreditationsInBio, showToolsInBio]
    .filter(Boolean).length

  const coachName = displayName || `${firstName} ${lastName}`
  const shopOfferings = isCoachProfilePersonaTabId(profileTab)
    ? getCoachProfilePersonaShopOfferings(offeringsByPersona, profileTab)
    : allOfferings
  const membershipDisplay = getCoachMembershipDisplayAccreditation({
    memberType,
    certificationLevel,
    admCertified: coach?.admCertified === true,
    pgaHopeCoach: coach?.coach?.pgaHopeCoach,
    hasPgaHopeProgram,
  })
  const showMembershipBanner = Boolean(membershipDisplay)

  // Intro video is embedded in the About intro column (legacy public profile pattern).
  const showBioRow = Boolean(bio)
  const bioCardHasContent = showBioRow
    || showIntroductionVideo
    || (!isMobile && hasStatsStrip)
    || showMembershipBanner
    || showSpecializedSkillsInBio
    || showAccreditationsInBio
    || showToolsInBio
    || showLocationsInBio
  const handleMembershipExplainerClose = useCallback(() => {
    setOpenAccreditationId(null)
  }, [])
  const showInPageContactForm = profileIsActive && showContactForm && showAboutSurface
  /**
   * Lead-collecting coaches without the Book a lesson widget get the Ask the
   * Coach card in that sticky md+ column. Both hosts are server-rendered and
   * CSS alone picks the visible one (sidebar from md, in-page section and
   * header pill below md), so first paint never moves the form. The in-page
   * form defers reCAPTCHA until it is visible; the sidebar mounts it only
   * after a goal is chosen.
   */
  const showAskTheCoachSidebar = !showBookingWidget && showInPageContactForm
  /** MUI v4 `down('sm')` is `(max-width:959.95px)`, the exact complement of `up('md')`. */
  const isBelowMd = useMediaQuery(theme.breakpoints.down('sm'))
  /** One accepted lead shows the confirmation in both hosts (no second lead after crossing md). */
  const [leadSentForProfileId, setLeadSentForProfileId] = useState(null)
  const markLeadSent = useCallback(
    () => setLeadSentForProfileId(coachProfileId),
    [coachProfileId],
  )
  const sharedLeadFormProps = showAskTheCoachSidebar
    ? { alreadySubmitted: leadSentForProfileId === coachProfileId, onSubmitted: markLeadSent }
    : {}
  const hasSidebar = showBookingWidget || showAskTheCoachSidebar
  const hasListedServices = Boolean(
    externalBookingUrl
    || hasShopOfferings
    || (!externalBookingUrl && offeringsLoading),
  )
  const hasFacilitiesSection = (coach?.facilities?.length ?? 0) > 1
  const sectionNavItems = buildCoachProfileBookingCardNavItems(coach, {
    hasAbout: bioCardHasContent,
    hasOfferings: showViewServices,
    hasContact: showContactForm,
    hasFacilities: hasFacilitiesSection,
  })

  const servicesNavItem = sectionNavItems.find((item) => item.id === 'services')
  const isNavItemHiddenFromMd = (item) => item.id === 'contact' && showAskTheCoachSidebar
  // A lone pill is not navigation. From md the Ask the Coach pill is CSS-hidden,
  // so hide the whole row there too when it would leave a single pill.
  const showSectionNav = sectionNavItems.length > 1
  const hideSectionNavFromMd = sectionNavItems
    .filter((item) => !isNavItemHiddenFromMd(item)).length <= 1

  const sectionNavKey = sectionNavItems.map((item) => `${item.id}:${item.anchorId}`).join('|')
  const [activeSectionId, setActiveSectionId] = useState(sectionNavItems[0]?.id)

  useEffect(() => {
    if (typeof window === 'undefined' || !sectionNavKey) return undefined
    const items = sectionNavKey.split('|').map((entry) => {
      const [itemId, anchorId] = entry.split(':')
      return { itemId, anchorId }
    })
    const activationOffset = 160
    let frame = null
    const isHidden = (el) => window.getComputedStyle(el).display === 'none'
    const update = () => {
      frame = null
      let current = items[0].itemId
      let closestTop = -Infinity
      items.forEach(({ itemId, anchorId }) => {
        const el = document.getElementById(anchorId)
        if (!el || isHidden(el)) return
        const { top } = el.getBoundingClientRect()
        if (top <= activationOffset && top > closestTop) {
          closestTop = top
          current = itemId
        }
      })
      const last = items[items.length - 1]
      const lastEl = document.getElementById(last.anchorId)
      const { scrollHeight } = document.documentElement
      const atPageBottom = scrollHeight > window.innerHeight
        && window.innerHeight + window.scrollY >= scrollHeight - 4
      if (
        atPageBottom
        && lastEl
        && !isHidden(lastEl)
        && lastEl.getBoundingClientRect().top < window.innerHeight
      ) {
        current = last.itemId
      }
      setActiveSectionId(current)
    }
    const onScroll = () => {
      if (frame == null) frame = window.requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame != null) window.cancelAnimationFrame(frame)
    }
  }, [sectionNavKey])

  const renderIntroVideoEmbed = () => (
    showIntroductionVideo ? (
      <Box
        id="coach-profile-intro-video"
        className={classes.introVideoEmbed}
        data-testid="coach-profile-intro-video"
      >
        <Embed
          data-gtm-element-name="coach-intro-video"
          data-gtm-coach-bookable={bookable}
          data-gtm-coach-location={`${city}${city && ', '}${state}`}
          data-gtm-coach-facility={facilities.length > 0 ? facilities[0].facilityName : null}
          data-gtm-coach-id={id}
          data-gtm-coach-profile-id={coachProfileId}
          data-gtm-profile-coach={displayName || `${firstName} ${lastName}`}
          data-gtm-intro-video-url={introductionVideoUrl}
          {...videoProps}
        />
      </Box>
    ) : null
  )

  const membershipBanner = (
    <CoachMembershipBanner
      accreditation={membershipDisplay}
      coachName={coachName}
      onReadMore={openAccreditationExplainer('membership_banner')}
    />
  )

  const bioCardBody = (
    <>
      {showMembershipBanner ? (
        <Box className={classes.membershipBannerBeforeBio}>
          {membershipBanner}
        </Box>
      ) : null}

      {renderIntroVideoEmbed()}

      {showBioRow && (
        <div className={classes.bioAndVideo}>
          <div className="bioFigures">
            <CoachBioMarkdown
              className={classes.bio}
              source={displayedBio}
              placeToggleAfter={isBioExpanded}
              trailingControl={bioIsLong ? (
                <button
                  type="button"
                  className={isBioExpanded
                    || coachBioEndsWithHyperlink(displayedBio)
                    || HTML_BLOCK_CLOSE.test(displayedBio)
                    ? `${classes.bioReadMoreButton} ${classes.bioReadMoreButtonAfter}`
                    : classes.bioReadMoreButton}
                  onClick={() => {
                    trackCoachProfileEvent('click-coach-profile-bio-read-more', {
                      expanded: !isBioExpanded,
                    })
                    setIsBioExpanded((prev) => !prev)
                  }}
                  aria-expanded={isBioExpanded}
                >
                  {isBioExpanded ? 'Read Less' : 'Read More'}
                </button>
              ) : null}
            />
          </div>
        </div>
      )}

      {showSpecializedSkillsInBio ? (
        <CoachSpecializedSkillsRow coach={coach} embedded />
      ) : null}

      {detailsColumnCount > 0 ? (
        <Box
          component="section"
          className={classes.detailsSection}
          aria-labelledby="coach-profile-details-heading"
          data-testid="coach-profile-details"
        >
          <Typography
            variant="h5"
            component="h2"
            id="coach-profile-details-heading"
            className={classes.detailsHeading}
          >
            Details
          </Typography>
          <Grid container spacing={3} className={classes.bioMetaLists}>
            {showLocationsInBio ? (
              <Grid item xs={12} sm={4}>
                <Box data-testid="coach-profile-details-locations">
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    className={classes.detailsColumnHeading}
                  >
                    {bookableLocations.length > 1 ? 'Locations' : 'Location'}
                  </Typography>
                  <Box component="ul" className={classes.detailsLocationList}>
                    {bookableLocations.map((location) => (
                      <li key={location.key} className={classes.detailsLocationItem}>
                        <a
                          href={location.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.detailsLocationLink}
                          onClick={() => trackCoachProfileEvent('click-coach-profile-location-directions', {
                            location_name: location.name || null,
                            position: 'details',
                          })}
                          aria-label={`Get directions to ${[location.name, location.address].filter(Boolean).join(', ')}`}
                        >
                          {location.name ? (
                            <span className={classes.detailsLocationName}>{location.name}</span>
                          ) : null}
                          {location.address ? (
                            <span className={classes.detailsLocationAddress}>{location.address}</span>
                          ) : null}
                        </a>
                      </li>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ) : null}
            {showAccreditationsInBio ? (
              <Grid item xs={12} sm={4}>
                <CoachAccreditationsRow
                  coach={{ ...coach, hasPgaHopeProgram }}
                  embedded
                  onOpenExplainer={openAccreditationExplainer('details')}
                />
              </Grid>
            ) : null}
            {showToolsInBio ? (
              <Grid item xs={12} sm={4}>
                <CoachToolsRow coach={coach} embedded />
              </Grid>
            ) : null}
          </Grid>
        </Box>
      ) : null}

      {renderStatsStrip(classes.bioCardFooter)}
    </>
  )

  const offeringsShop = (
    <CoachProfileOfferingsShop
      key={profileTab}
      id={COACH_PROFILE_OFFERINGS_ANCHOR_ID}
      offerings={shopOfferings}
      loading={Boolean(offeringsLoading)}
      externalBookingUrl={externalBookingUrl || null}
    />
  )

  const bioColumn = bioCardHasContent ? (
    <BioWrapper className={classes.bioSectionCard}>
      {bioCardBody}
    </BioWrapper>
  ) : null

  const aboutColumn = (
    <Box
      id="coach-profile-about"
      role="region"
      aria-label="About"
      style={showProfileTabs && isPersonaTab ? { display: 'none' } : undefined}
      aria-hidden={showProfileTabs && isPersonaTab}
    >
      {bioColumn}
    </Box>
  )

  const aboutContentColumn = (
    <CoachProfileInColumn>
      {showAboutSurface ? (
        <>
          {offeringsShop}
          {children}
        </>
      ) : null}
      {showProfileTabs && isCoachProfilePersonaTabId(profileTab) && (
        <Box
          id="coach-profile-tabpanel-persona"
          role="tabpanel"
          aria-label={PERSONA_PROFILE_TAB_LABELS[profileTab]}
        >
          {offeringsShop}
        </Box>
      )}
      {showInPageContactForm ? (
        <Box
          id={COACH_PROFILE_CONTACT_ANCHOR_ID}
          role="region"
          aria-label="Contact the Coach"
          className={[
            classes.contactButtonContainer,
            classes.contactButtonContainerInTab,
            classes.profileContactSection,
            showAskTheCoachSidebar ? classes.profileContactSectionHiddenFromMd : null,
          ].filter(Boolean).join(' ')}
          style={{ scrollMarginTop: 120 }}
          data-testid="coach-profile-contact"
        >
          <CoachProfileLeadForm
            key={`coach-profile-lead-${coach.coachProfileId}`}
            coach={coach}
            barePageLayout
            hasListedServices={hasListedServices}
            {...(showAskTheCoachSidebar ? {
              deferRecaptchaUntilVisible: true,
              recaptchaHostVisible: isBelowMd,
            } : {})}
            {...sharedLeadFormProps}
          />
        </Box>
      ) : null}
      {renderSocialLinks(
        classes.contentColumnSocial,
        'coach-profile-content-social',
      )}
    </CoachProfileInColumn>
  )

  return (
    <div className={classes.outerContainer}>
      <Container maxWidth={false} disableGutters className={classes.simplePage}>
        <div className={classes.contentCanvas}>
          <div
            className={hasSidebar
              ? `${classes.profileBody} ${classes.profileBodyWithSidebar}`
              : classes.profileBody}
          >
            <Box component="header" className={`${classes.profileHeader} ${classes.profileBodyHeader}`} data-testid="coach-profile-header">
              <Box className={classes.profileHeaderIdentity}>
                {profilePhoto ? (
                  <Box className={classes.profileHeaderPhotoWrap}>
                    <img
                      src={composeOptimizedImageUrl(secureLink(profilePhoto), 280)}
                      alt=""
                      className={classes.profileHeaderPhoto}
                    />
                  </Box>
                ) : null}
                <Box className={classes.profileHeaderText}>
                  <Typography
                    variant="h2"
                    component="h1"
                    className={classes.profileHeaderName}
                  >
                    {coachName}
                  </Typography>
                  {title ? (
                    <Typography
                      component="p"
                      className={classes.profileHeaderTitle}
                      data-testid="coach-profile-title"
                    >
                      {title}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              {showSectionNav ? (
                <>
                  <Box
                    component="nav"
                    className={[
                      classes.sectionNav,
                      hideSectionNavFromMd ? classes.sectionNavHiddenFromMd : null,
                    ].filter(Boolean).join(' ')}
                    aria-label={`${coachName} profile sections`}
                    data-testid="coach-profile-section-nav"
                  >
                    {sectionNavItems.map((item) => (
                      <Button
                        key={item.id}
                        href={preserveQueryOnHref(item.href, router.asPath)}
                        className={[
                          classes.sectionNavButton,
                          activeSectionId === item.id ? classes.sectionNavButtonActive : null,
                          isNavItemHiddenFromMd(item)
                            ? classes.sectionNavButtonHiddenFromMd
                            : null,
                        ].filter(Boolean).join(' ')}
                        aria-current={activeSectionId === item.id ? 'location' : undefined}
                        onClick={(event) => {
                          event.preventDefault()
                          setActiveSectionId(item.id)
                          trackCoachProfileEvent('click-coach-profile-section-nav', {
                            section: item.id,
                            label: item.label,
                          })
                          if (item.id === 'contact') {
                            gtmEvent({
                              event: 'click-contact-button',
                              attributes: {
                                coach_name: coachName,
                                coach_profile_id: coachProfileId,
                                contact_button_label: 'Send a Request',
                              },
                            })
                          }
                          scrollToProfileSection(item.anchorId)
                        }}
                        {...(item.id === 'contact' ? {
                          'data-gtm-button-name': 'consolidated-contact-bookable-coach',
                          'data-gtm-profile-coach': coachName,
                          'data-gtm-cta-position': 'profile-header',
                        } : {})}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                </>
              ) : null}
            </Box>

            {showBookingWidget ? (
              <Box
                component="aside"
                className={classes.profileBodySidebar}
                aria-label={`Book a lesson with ${coachName}`}
                data-testid="coach-profile-booking-column"
              >
                <CoachProfileBookingWidget
                  coach={coach}
                  offerings={bookablePrivateLessons}
                  sidebar
                />
                {servicesNavItem ? (
                  <Button
                    href={preserveQueryOnHref(servicesNavItem.href, router.asPath)}
                    className={classes.coachingOptionsCta}
                    data-gtm-button-name="coaching-options"
                    data-gtm-cta-position="booking-widget"
                    onClick={(event) => {
                      event.preventDefault()
                      setActiveSectionId(servicesNavItem.id)
                      trackCoachProfileEvent('click-coach-profile-coaching-options', {
                        section: servicesNavItem.id,
                        position: 'booking-widget',
                      })
                      scrollToProfileSection(servicesNavItem.anchorId)
                    }}
                  >
                    See All Services
                  </Button>
                ) : null}
              </Box>
            ) : null}
            {showAskTheCoachSidebar ? (
              <Box
                component="aside"
                className={`${classes.profileBodySidebar} ${classes.profileBodySidebarFromMd}`}
                aria-label={`Ask ${coachName} a question`}
                data-testid="coach-profile-ask-the-coach-column"
              >
                <CoachProfileLeadForm
                  key={`coach-profile-sidebar-lead-${coach.coachProfileId}`}
                  coach={coach}
                  sidebar
                  {...sharedLeadFormProps}
                />
              </Box>
            ) : null}
            <Box className={classes.profileBodyIntro} data-testid="coach-profile-content-column">
              {aboutColumn}
            </Box>
            <Box className={classes.profileBodyMain} data-testid="coach-profile-main-column">
              {aboutContentColumn}
            </Box>
          </div>
        </div>
      </Container>
      <CoachAccreditationExplainerDialog
        accreditationId={openAccreditationId}
        onClose={handleMembershipExplainerClose}
        onCtaClick={(cta) => trackCoachProfileEvent('click-coach-accreditation-explainer-cta', {
          accreditation_id: openAccreditationId,
          cta_label: cta.label,
          href: cta.href || null,
        })}
      />
    </div>
  )
}

CoachBio.propTypes = {
  profileTab: PropTypes.string,
  onProfileTabChange: PropTypes.func,
  /**
   * Parent hash-scroll (View Services, Send a Request). Omit in isolation;
   * CoachBio then scrolls the matching in-page anchor itself.
   */
  onScrollToSection: PropTypes.func,
  /**
   * About-page stack that should sit in the content column (right from md)
   * so the booking card can stay sticky for the whole profile (photos, services,
   * articles, testimonials, facilities). The lead form is additional —
   * it does not replace this stack. The membership banner sits
   * above the bio paragraphs (under the tagline), before Tools &
   * Technology and PGA Accreditations (accreditation bullets open
   * the shared explainer). Specialized skills chips sit immediately
   * below the rendered bio and its Read More / Read Less control,
   * before Details. There is no About Coach heading.
   */
  children: PropTypes.node,
  offeringsLoading: PropTypes.bool,
  offeringsByPersona: PropTypes.objectOf(PropTypes.array),
  /**
   * Whether the coach-profile accreditations experience is enabled
   * (`flags.FLAG_COACH_ACCREDITATIONS`). When true, renders a
   * bulleted **PGA Accreditations** list after the bio (and after the
   * membership banner above it), beside Tools & Technology
   * when that list has items. Omit the heading and list when empty.
   * The booking card does not show a membership pill. The bio
   * membership banner and PGA Accreditations bullets use the shared
   * explainer dialog. ADM and PGA HOPE surface only in the PGA
   * Accreditations bullets. Wired from `coaches-page.jsx`.
   */
  coachAccreditationsEnabled: PropTypes.bool,
  hasPgaHopeProgram: PropTypes.bool,
  coach: PropTypes.shape(
    {
      coachProfileId: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      displayName: PropTypes.string,
      customSlug: PropTypes.string,
      certificationLevel: PropTypes.string,
      memberType: PropTypes.string,
      admCertified: PropTypes.bool,
      title: PropTypes.string,
      profilePhoto: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
      bio: PropTypes.string,
      numberOfStudents: PropTypes.number,
      numberOfFollowers: PropTypes.number,
      yearsOfPgaMembership: PropTypes.number,
      socialJsonData: PropTypes.array,
      testimonialsJsonData: PropTypes.array,
      photosJsonData: PropTypes.arrayOf(PropTypes.shape({
        reference: PropTypes.string,
        rank: PropTypes.number,
      })),
      toolJsonData: PropTypes.array,
      skills: PropTypes.arrayOf(PropTypes.shape({
        skillName: PropTypes.string,
      })),
      facilities: PropTypes.array,
      introductionVideoUrl: PropTypes.string,
      bookable: PropTypes.bool,
      bookingUrl: PropTypes.string,
      status: PropTypes.string,
      coach: PropTypes.shape({
        id: PropTypes.string,
        freeTierLimitsReached: PropTypes.array,
        pgaHopeCoach: PropTypes.bool,
        preferences: PropTypes.shape({
          bookNowEnabled: PropTypes.bool,
          acceptsLeads: PropTypes.bool,
        }),
      }),
    },
  ),
}

CoachBio.defaultProps = {
  profileTab: undefined,
  onProfileTabChange: undefined,
  onScrollToSection: undefined,
  children: null,
  offeringsLoading: false,
  offeringsByPersona: {},
  coachAccreditationsEnabled: false,
}

export default CoachBio
